import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Eye, Wallet } from 'lucide-react';
import Alert from '../common/Alert';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import SettlementModalShell from '../common/SettlementModalShell.js';
import SettlementFieldList from '../common/SettlementFieldList.js';
import AgentCommissionLedger from '../common/AgentCommissionLedger.js';
import { filterDateInputClass, filterInputClass, filterSelectClass } from '../common/uiClasses.js';
import { DashboardLayout } from '../common/page_component/index.js';
import { fullCurrency } from '../../utils/formatCurrency.js';
import { formatIstDateTime } from '../../utils/localDate.js';
import { formatAuditUserHubRole } from '../../utils/userDisplay.js';
import { useAuth } from '../../context/AuthContext.js';
import { isContractPartyAdmin } from '../../utils/networkMode.js';
import mlAgentCommissionService from '../../services/mlAgentCommissionService.js';

export function CommissionPaymentsEntry() {
  const { user } = useAuth();
  if (isContractPartyAdmin(user)) return <AgentCommissionPaymentsPage />;
  if (user?.role === 'Hub Admin') return <Navigate to="/company-settlements" replace />;
  return <Navigate to="/company-settlement-approvals" replace />;
}

const payableAmount = (r) => {
  if (r?.net_amount != null && r.net_amount !== '') return Number(r.net_amount || 0);
  return Number(r?.commission_amount || 0);
};
const tdsAmount = (r) => Number(r?.tds_amount || 0);

const AgentCommissionPaymentsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isCpa = isContractPartyAdmin(user);
  const homePath = isCpa ? '/contract-party-dashboard' : '/ml-settlement-approvals';

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [agentUserId, setAgentUserId] = useState(() => searchParams.get('agent_user_id') || '');
  const [partyScope, setPartyScope] = useState(isCpa ? 'party' : 'all');
  const [agents, setAgents] = useState([]);
  const [rows, setRows] = useState([]);
  const [totalUnpaid, setTotalUnpaid] = useState(0);
  const [totalPaid, setTotalPaid] = useState(0);
  const [listTab, setListTab] = useState('unpaid');
  const [viewEntry, setViewEntry] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [paymentMode, setPaymentMode] = useState('cash');
  const [remarks, setRemarks] = useState('');

  const loadAgents = useCallback(async () => {
    try {
      const res = await mlAgentCommissionService.listAgents();
      setAgents(res?.data || []);
    } catch {
      setAgents([]);
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (agentUserId) params.agent_user_id = agentUserId;
      if (!isCpa && partyScope && partyScope !== 'all') params.party_scope = partyScope;
      if (listTab === 'paid') {
        const res = await mlAgentCommissionService.listPaid(params);
        setRows(res?.data || []);
        setTotalPaid(res?.total_paid || 0);
      } else {
        const res = await mlAgentCommissionService.listUnpaid(params);
        setRows(res?.data || []);
        setTotalUnpaid(res?.total_unpaid || 0);
      }
      setSelected(new Set());
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load commissions');
      setRows([]);
      setTotalUnpaid(0);
      setTotalPaid(0);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, agentUserId, partyScope, isCpa, listTab]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const selectedTotal = useMemo(
    () =>
      rows
        .filter((r) => selected.has(r.id))
        .reduce((sum, r) => sum + payableAmount(r), 0),
    [rows, selected],
  );

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map((r) => r.id)));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePay = async () => {
    if (selected.size === 0) {
      Alert.error('Select at least one commission entry');
      return;
    }
    const confirmed = await Alert.confirm(
      `Pay ${fullCurrency(selectedTotal)} (net after TDS) for ${selected.size} commission ${selected.size === 1 ? 'entry' : 'entries'}? This creates a cash expense and marks them paid.`,
      'Confirm commission payment',
    );
    if (!confirmed) return;
    try {
      setPaying(true);
      const res = await mlAgentCommissionService.pay({
        entry_ids: Array.from(selected),
        payment_mode: paymentMode,
        remarks,
      });
      Alert.success(res?.message || 'Commission paid and expense created');
      await loadAgents();
      await loadRows();
      setRemarks('');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Payment failed');
    } finally {
      setPaying(false);
    }
  };

  return (
    <DashboardLayout>
      <ListPageHeader
        title="Booking & Delivery Agent Commission"
        subtitle={
          isCpa
            ? 'Pay unpaid commission for booking and delivery agents when it was not deducted on settlement'
            : 'Pay unpaid commission for booking and delivery agents with commission enabled (creates cash expense if not already deducted on To Pay settlement)'
        }
        backTo={homePath}
      >
        <Button variant="secondary" onClick={() => navigate(homePath)}>
          {isCpa ? 'Back to Dashboard' : 'Back to Approvals'}
        </Button>
      </ListPageHeader>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3 sm:p-4 mb-4">
        <div className="flex flex-col gap-3 md:flex-row md:flex-nowrap md:items-end md:gap-2 md:overflow-x-auto">
          <div className="w-full md:w-auto md:shrink-0">
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <div className="flex flex-wrap gap-1.5 p-1 rounded-xl bg-gray-50 border border-gray-200">
              {[
                { id: 'unpaid', label: 'Unpaid' },
                { id: 'paid', label: 'Paid' },
              ].map((tab) => {
                const active = listTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setListTab(tab.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold transition-colors ${
                      active
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-white/70'
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 md:contents min-w-0">
            <div className="min-w-0 md:w-[9.5rem] md:shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">From date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={`${filterDateInputClass} date-input-compact`}
              />
            </div>
            <div className="min-w-0 md:w-[9.5rem] md:shrink-0">
              <label className="block text-xs font-medium text-gray-600 mb-1">To date</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={`${filterDateInputClass} date-input-compact`}
              />
            </div>
          </div>

          <div className="text-sm text-gray-700 md:pb-2 md:shrink-0 md:whitespace-nowrap">
            {listTab === 'paid' ? 'Total paid:' : 'Total unpaid:'}{' '}
            <span className="font-semibold tabular-nums">
              {fullCurrency(listTab === 'paid' ? totalPaid : totalUnpaid)}
            </span>
          </div>

          <div className={`grid gap-2 md:contents min-w-0 ${isCpa ? 'grid-cols-1' : 'grid-cols-2'}`}>
            <div className="min-w-0 w-full md:flex-1 md:min-w-[9rem]">
              <label className="block text-xs font-medium text-gray-600 mb-1">Agent</label>
              <select
                value={agentUserId}
                onChange={(e) => setAgentUserId(e.target.value)}
                className={filterSelectClass}
              >
                <option value="">All agents</option>
                {agents.map((a) => (
                  <option key={a.user_id} value={a.user_id}>
                    {a.name} ({a.agent_scope}) — {fullCurrency(a.unpaid_amount)}
                  </option>
                ))}
              </select>
            </div>
            {!isCpa && (
              <div className="min-w-0 w-full md:w-36 md:shrink-0">
                <label className="block text-xs font-medium text-gray-600 mb-1">Scope</label>
                <select
                  value={partyScope}
                  onChange={(e) => setPartyScope(e.target.value)}
                  className={filterSelectClass}
                >
                  <option value="all">All</option>
                  <option value="own">Own company</option>
                  <option value="party">Contracted party</option>
                </select>
              </div>
            )}
          </div>

          {listTab === 'unpaid' && (
            <>
              <div className="grid grid-cols-2 gap-2 md:contents min-w-0">
                <div className="min-w-0 md:w-36 md:shrink-0">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Payment mode</label>
                  <select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    className={filterSelectClass}
                  >
                    <option value="cash">Cash</option>
                    <option value="upi">UPI</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                  </select>
                </div>
                <div className="min-w-0 md:flex-1 md:min-w-[8rem]">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Remarks</label>
                  <input
                    type="text"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    className={filterInputClass}
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div className="flex items-end gap-2 md:contents">
                <div className="text-sm text-gray-700 md:pb-2 md:shrink-0 md:whitespace-nowrap">
                  Selected: <span className="font-semibold tabular-nums">{fullCurrency(selectedTotal)}</span>
                </div>
                <Button
                  variant="primary"
                  icon={paying ? undefined : selected.size ? Check : Wallet}
                  disabled={paying || selected.size === 0}
                  onClick={handlePay}
                  className="ml-auto md:ml-0 w-auto shrink-0 whitespace-nowrap"
                >
                  {paying ? 'Paying…' : 'Pay Selected'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <Loader fullScreen={false} message="Loading commissions..." />
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">
              {listTab === 'paid' ? 'No paid commissions for this period' : 'No unpaid commissions'}
            </p>
          ) : (
            <>
              <div className="hidden xl:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      {listTab === 'unpaid' && (
                        <th className="px-4 py-2.5 text-left">
                          <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                        </th>
                      )}
                      <th className="text-left px-4 py-2.5 font-semibold">Agent</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Scope</th>
                      <th className="text-left px-4 py-2.5 font-semibold">ML Sheet</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Side</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Freight</th>
                      <th className="text-right px-4 py-2.5 font-semibold">%</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Gross</th>
                      <th className="text-right px-4 py-2.5 font-semibold">TDS</th>
                      <th className="text-right px-4 py-2.5 font-semibold">Net</th>
                      <th className="text-left px-4 py-2.5 font-semibold">Settled by</th>
                      {listTab === 'paid' && (
                        <th className="text-left px-4 py-2.5 font-semibold">Paid at</th>
                      )}
                      <th className="text-center px-4 py-2.5 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r) => (
                      <tr
                        key={r.id}
                        className="hover:bg-sky-50/50 cursor-pointer"
                        onClick={() => setViewEntry(r)}
                      >
                        {listTab === 'unpaid' && (
                          <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selected.has(r.id)}
                              onChange={() => toggleOne(r.id)}
                            />
                          </td>
                        )}
                        <td className="px-4 py-2.5 font-medium">{r.agent_name || r.agent_user_id}</td>
                        <td className="px-4 py-2.5 text-xs">
                          {r.is_contracted_party
                            ? `Contracted${r.party_name ? ` · ${r.party_name}` : ''}`
                            : 'Own company'}
                        </td>
                        <td className="px-4 py-2.5">{r.ml_sheet_id}</td>
                        <td className="px-4 py-2.5 capitalize">{r.side}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(r.freight_base)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{r.commission_pct}%</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fullCurrency(r.commission_amount)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                          {tdsAmount(r) > 0
                            ? `${fullCurrency(tdsAmount(r))}${r.tds_pct ? ` (${r.tds_pct}%)` : ''}`
                            : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-right font-semibold tabular-nums">
                          {fullCurrency(payableAmount(r))}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-800 break-words max-w-[180px]">
                          {listTab === 'paid' ? formatAuditUserHubRole(r, 'paid_by') : 'Awaiting payout'}
                        </td>
                        {listTab === 'paid' && (
                          <td className="px-4 py-2.5 text-gray-600 text-xs">{formatIstDateTime(r.paid_at)}</td>
                        )}
                        <td className="px-4 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                          <Button variant="secondary" size="sm" icon={Eye} onClick={() => setViewEntry(r)}>
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="xl:hidden space-y-3 p-3 sm:p-4">
                {listTab === 'unpaid' && (
                  <label className="flex items-center gap-2 text-sm text-gray-700 px-1">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    <span>Select all</span>
                  </label>
                )}
                {rows.map((r) => (
                  <div
                    key={`card-${r.id}`}
                    className={`rounded-xl border border-gray-100 border-l-4 shadow-sm p-4 cursor-pointer ${
                      selected.has(r.id) ? 'border-l-blue-600 bg-blue-50/40' : 'border-l-blue-500 bg-white'
                    }`}
                    onClick={() => setViewEntry(r)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <label className="flex items-start gap-2 min-w-0 flex-1" onClick={(e) => listTab === 'unpaid' && e.stopPropagation()}>
                        {listTab === 'unpaid' && (
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={selected.has(r.id)}
                            onChange={() => toggleOne(r.id)}
                          />
                        )}
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 text-sm break-words">
                            {r.agent_name || r.agent_user_id}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5 break-all">{r.ml_sheet_id}</p>
                        </div>
                      </label>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 capitalize">
                          {r.side}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-50 text-gray-700 border border-gray-200">
                          {r.is_contracted_party ? 'Contracted' : 'Own'}
                        </span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                      <div>
                        <span className="text-gray-500 block">Freight</span>
                        <p className="font-medium tabular-nums">{fullCurrency(r.freight_base)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Commission %</span>
                        <p className="font-medium tabular-nums">{r.commission_pct}%</p>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Gross</span>
                        <p className="font-medium tabular-nums">{fullCurrency(r.commission_amount)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500 block">TDS</span>
                        <p className="font-medium tabular-nums">
                          {tdsAmount(r) > 0
                            ? `${fullCurrency(tdsAmount(r))}${r.tds_pct ? ` (${r.tds_pct}%)` : ''}`
                            : '—'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500 block">Net payable</span>
                        <p className="font-semibold text-gray-900 tabular-nums">{fullCurrency(payableAmount(r))}</p>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500 block">Settled by</span>
                        <p className="font-medium text-gray-800">
                          {listTab === 'paid' ? formatAuditUserHubRole(r, 'paid_by') : 'Awaiting payout'}
                        </p>
                      </div>
                      {listTab === 'paid' && (
                        <div className="col-span-2">
                          <span className="text-gray-500 block">Paid at</span>
                          <p className="font-medium text-gray-800">{formatIstDateTime(r.paid_at)}</p>
                        </div>
                      )}
                      {r.is_contracted_party && r.party_name && (
                        <div className="col-span-2">
                          <span className="text-gray-500 block">Party</span>
                          <p className="font-medium text-gray-800 break-words">{r.party_name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {viewEntry && (
        <SettlementModalShell
          size="sm"
          title={viewEntry.status === 'PAID' ? 'Paid commission' : 'Unpaid commission'}
          subtitle={viewEntry.ml_sheet_id}
          onClose={() => setViewEntry(null)}
          footer={(
            <div className="flex justify-end">
              <Button variant="secondary" onClick={() => setViewEntry(null)}>Close</Button>
            </div>
          )}
        >
          <div className="space-y-3">
            <SettlementFieldList
              items={[
                { label: 'Agent', value: viewEntry.agent_name || viewEntry.agent_user_id },
                { label: 'Status', value: viewEntry.status === 'PAID' ? 'Paid' : 'Unpaid' },
                { label: 'Location hub', value: viewEntry.location_hub_name || '—' },
                {
                  label: 'Settled by',
                  value: viewEntry.status === 'PAID'
                    ? formatAuditUserHubRole(viewEntry, 'paid_by')
                    : 'Awaiting payout',
                },
                { label: 'Paid at', value: formatIstDateTime(viewEntry.paid_at) },
                viewEntry.expense_id ? { label: 'Expense', value: viewEntry.expense_id } : null,
              ]}
            />
            <AgentCommissionLedger
              totalFreight={viewEntry.freight_base}
              commissionAmount={viewEntry.commission_amount}
              commissionPct={viewEntry.commission_pct}
              tdsAmount={tdsAmount(viewEntry)}
              tdsPct={viewEntry.tds_pct}
              netToAgent={payableAmount(viewEntry)}
              netDue={payableAmount(viewEntry)}
              netDueLabel="Net payable"
              positiveTone="emerald"
            />
          </div>
        </SettlementModalShell>
      )}
    </DashboardLayout>
  );
};

export default AgentCommissionPaymentsPage;
